import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RIGHT_DRAWER_NS } from './i18n';
import { BunkeringData } from './types';
import { getDateRangeText } from '../../utils/dateUtils';
import { useNumberFormatter } from '@diana/core';
import { FlowDirectionDisplay } from './FlowDirectionDisplay';

type DeliverySummaryProps = {
  data: BunkeringData | Record<string, never>;
};

export const DeliverySummary = ({ data }: DeliverySummaryProps) => {
  const { t } = useTranslation([RIGHT_DRAWER_NS]);
  const { palette, breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down(700));
  const { format: numberFormat } = useNumberFormatter(4, true);

  const commonBoxStyles = {
    mb: 1,
    pr: 1,
    borderRight: isMobile ? 'none' : `1px solid ${palette.divider}`,
  };

  return (
    <div>
      <Typography
        color={palette.text.primary}
        sx={{ fontSize: '16px', lineHeight: '28px', fontWeight: 500 }}
      >
        {t('components/RightDrawer:details')}
      </Typography>
      <Box 
        sx={{ 
          mt: 1, 
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        {/* Barge Name */}
        <Box sx={commonBoxStyles}>
          <Typography color={palette.text.secondary}>
            {t('components/RightDrawer:bargeName')}
          </Typography>
          <Typography color={palette.text.primary} sx={{ fontWeight: 500 }}>
            {data?.bargeInfo?.name || '-'}
          </Typography>
        </Box>

        {/* MFM Name */}
        <Box sx={commonBoxStyles}>
          <Typography color={palette.text.secondary}>
            {t('components/RightDrawer:mfmName')}
          </Typography>
          <Typography color={palette.text.primary} sx={{ fontWeight: 500 }}>
            {data?.deviceInfo?.name || '-'}
          </Typography>
        </Box>

        {/* Start and End Date */}
        <Box sx={commonBoxStyles}>
          <Typography color={palette.text.secondary}>
            {t('components/RightDrawer:startAndEndDate')}
          </Typography>
          <Typography color={palette.text.primary} sx={{ fontWeight: 500 }}>
            {getDateRangeText(
              [data?.bunkerActivePeriod?.start?.date, data?.bunkerActivePeriod?.end?.date],
              'ff',
            )}
          </Typography>
        </Box>

        {/* Direction */}
        <Box sx={commonBoxStyles}>
          <Typography color={palette.text.secondary}>
            {t('components/RightDrawer:direction')}
          </Typography>
          <FlowDirectionDisplay 
            flowDirection={data?.flowDirection} 
          />
        </Box>

        {/* Begin forward */}
        <Box sx={commonBoxStyles}>
          <Typography color={palette.text.secondary}>
            {t('components/RightDrawer:beginForward')}
          </Typography>
          <Typography color={palette.text.primary} sx={{ fontWeight: 500 }}>
            {numberFormat(data?.massInventory.begin.forward)}
          </Typography>
        </Box>

        {/* End forward */}
        <Box sx={commonBoxStyles}>
          <Typography color={palette.text.secondary}>
            {t('components/RightDrawer:endForward')}
          </Typography>
          <Typography color={palette.text.primary} sx={{ fontWeight: 500 }}>
            {numberFormat(data?.massInventory.end.forward)}
          </Typography>
        </Box>

        {/* Begin reverse*/}
        <Box sx={commonBoxStyles}>
          <Typography color={palette.text.secondary}>
            {t('components/RightDrawer:beginReverse')}
          </Typography>
          <Typography color={palette.text.primary} sx={{ fontWeight: 500 }}>
            {numberFormat(data?.massInventory.begin.reverse)}
          </Typography>
        </Box>

        {/* End reverse*/}
        <Box sx={commonBoxStyles}>
          <Typography color={palette.text.secondary}>
            {t('components/RightDrawer:endReverse')}
          </Typography>
          <Typography color={palette.text.primary} sx={{ fontWeight: 500 }}>
            {numberFormat(data?.massInventory.end.reverse)}
          </Typography>
        </Box>

        {/* Average density*/}
        <Box sx={commonBoxStyles}>
          <Typography color={palette.text.secondary}>
            {t('components/RightDrawer:averageDensity')}
          </Typography>
          <Typography color={palette.text.primary} sx={{ fontWeight: 500 }}>
            {numberFormat(data?.averageDensity)}
          </Typography>
        </Box>

        {/* Aeration limit*/}
        <Box sx={{ ...commonBoxStyles, borderRight: 'none' }}>
          <Typography color={palette.text.secondary}>
            {t('components/RightDrawer:aerationLimit')}
          </Typography>
          <Typography color={palette.text.primary} sx={{ fontWeight: 500 }}>
            {numberFormat(data?.aerationLimit)}
          </Typography>
        </Box>
      </Box>
    </div>
  );
};
